import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchRewardsTransactionRequest,
  redeemRewardsPointsAdjustmentsDetailsRequest,
  rewardsPercentageDateRequest,
  RootState,
  getFirstAndLastDate,
  useraccountDetailsRequest,
  rewardsPercentageDateFailure,
  webFormatCurrency,
  contactInfoRequest,
  getRewardsHistoryEarnedCapBannerMessage,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from './BreadCrumbs';
import Spinner from './Spinner';
import moment from 'moment';
import FlippedToolTip from './FlippedToolTip';
import CardDropdown from './CardDropdown';
import CommanToolTip from './CommanToolTip';
import { EventType, useAnalytics } from '../ui/Analytics';
import { logBrazeCustomEvent } from '../config/braze';

interface RewardsTransaction {
  basePointsEarned: number;
  merchant: any;
  description: string;
  productCode?: string;
  billingAmount?: number;
  promotions?: Array<{
    pointType: string;
    pointsEarned: number;
    promoEligible?: boolean;
    id?: string;
  }>;
  postingDate: any;
  sequenceNumber: any;
}

interface AdjustmentsType {
  adjustmentReason: string;
  adjustmentValue: number;
  adjustments: Array<{
    adjustmentValue: number;
  }>;
  adjustmentDate: any;
}

const ViewRewards = () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData;
  const { rb_common, rb_earned_rewards_history, rb_transaction_history, rb_feature_flagging } =
    entries;
  const { accountId, customerId, accountDetails } = useSelector(
    (state: RootState) => state.user
  );
  const { contactDetails, loading } = useSelector(
    (state: RootState) => state.contactInfo
  );
  const {
    rewardsTransactionList,
    rewardsTransactionLoding,
    rewardsPointsAdjustmentsDetails,
    rewardsPercentageDate,
    rewardsPercentageDateLoading,
    rewardsPointsAdjustmentsDetailsloading,
  } = useSelector((state: RootState) => state.redeemRewards);
  const lang = localStorage.getItem('language') || 'en-US';
  const currentUser = `${accountDetails?.customer?.firstName}${' '}${accountDetails?.customer?.lastName}`;

  const setupLocale = () => {
    try {
      const localeToUse = lang?.includes('fr') ? 'fr' : 'en';
      moment.locale(localeToUse);
      return localeToUse;
    } catch (error) {
      console.warn('Error setting moment locale:', error);
      moment.locale('en');
      return 'en';
    }
  };

  const locale = setupLocale();

  const formatDate = (dateString: any, formatPattern: any) => {
    try {
      return moment(dateString, 'YYYY-MM-DD')
        .locale(locale)
        .format(formatPattern);
    } catch (error) {
      console.warn('Error formatting date with moment:', error);
      return moment(dateString).format('MM/DD/YYYY');
    }
  };

  const calculateCurrentMonthFromList = (uniqueMonthDates: any) => {
    const dates = Array.isArray(uniqueMonthDates) ? uniqueMonthDates : [];

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = '01';
    const currentMonthDate = `${year}-${month}-${day}`;
    if (!dates?.includes(currentMonthDate)) {
      return [currentMonthDate, ...dates];
    }
    return dates;
  };
  const latestMonthsList = calculateCurrentMonthFromList(
    accountDetails?.cycleDates
  );
  const seenMonths = new Set();
  const uniqueMonthDates = latestMonthsList.filter((date: any) => {
    const monthKey = date.slice(0, 7); // "YYYY-MM"
    if (seenMonths.has(monthKey)) {
      return false;
    }
    seenMonths.add(monthKey);
    return true;
  });
  const getMonthList = uniqueMonthDates
    ? uniqueMonthDates.map((date: any) => {
        try {
          return moment(date).locale(locale).format('MMMM YYYY');
        } catch (error) {
          console.warn('Error formatting cycle date:', error);
          return moment(date).format('MM/YYYY');
        }
      })
    : [];

  const months = useMemo(() => {
    const first12Months = getMonthList.slice(0, 12); // only first 12 months
    const yearToDate = rb_earned_rewards_history?.rewardsHistory?.yearToDate;

    if (first12Months.length === 0) {
      // fallback to current month and yearToDate
      return [
        (() => {
          try {
            return moment().locale(locale).format('MMMM YYYY');
          } catch (error) {
            console.warn('Error formatting fallback month:', error);
            return moment().format('MM/YYYY');
          }
        })(),
        yearToDate,
      ];
    }

    return [...first12Months, yearToDate];
  }, [getMonthList, rb_earned_rewards_history, locale]);

  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [availableCards, setAvailableCards] = useState<any>([]);
  const [selectedPeriod, setSelectedPeriod] = useState(months[0] || '');
  const [isMonthsDropdownOpen, setIsMonthsDropdownOpen] = useState(false);
  const [cashBackPercent, setCashBackPercent] = useState('');
  const [cashBackInfoTipDetails, setCashBackInfoTipDetails] = useState('');

  const [isThisMonth, setIsThisMonth] = useState(0);
  const [isBonus, setIsBonus] = useState(0);
  const [isEarnedRewards, setIsEarnedRewards] = useState(0);
  const [isAdjustments, setIsAdjustments] = useState(0);
  const [isRewardsTransactionList, setRewardsTransactionList] = useState<
    RewardsTransaction[]
  >([]);
  const [isAdjustmentDataList, setIsAdjustmentDataList] = useState<
    AdjustmentsType[]
  >([]);
  const [activeTile, setActiveTile] = useState<
    null | 'month' | 'bonus' | 'earnedReward' | 'adjustment'
  >(null);
  const [showTooltipMobile, setShowTooltipMobile] = useState<
    null | 'month' | 'bonus' | 'earnedReward' | 'adjustment'
  >(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);
  const [UiReady, setUiReady] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleDropdownToggle = () => {
    setIsMonthsDropdownOpen((prev) => !prev);
  };

  const handleMonthSelect = (month: string) => {
    setSelectedPeriod(month);
    setIsMonthsDropdownOpen(false);
    trackPageAndClickEvent(month);
  };

  // useEffect(() => {
  //   trackEvent(
  //     EventType.PAGE_VIEW,
  //     ['earned cash back rewards', 'rewards'],
  //     window.location.href,
  //     undefined,
  //     false
  //   );
  // }, []);
  useEffect(() => {
    logBrazeCustomEvent('viewed_rewards_history');
  }, []);

  const trackPageAndClickEvent = (month: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['earned cash back rewards', 'rewards'],
      window.location.href,
      `earned cash back rewards | period-${month}`
    );
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMonthsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (accountId && customerId) {
      dispatch(useraccountDetailsRequest({ accountId, customerId }));

      dispatch(rewardsPercentageDateRequest(accountId));

      dispatch(
        contactInfoRequest({
          accountId: accountId,
          customerId: customerId,
          primaryCustomerId: customerId,
        })
      );
    }
  }, [dispatch, accountId, customerId]);

  useEffect(() => {
    setUiReady(false);
    if (selectedCard && selectedPeriod && accountDetails?.accountId && locale) {
      updateStateAfterSelectionDropDown();
    }
  }, [selectedCard, selectedPeriod, offset, accountDetails?.accountId, locale]);

  useEffect(() => {
    if (
      !rewardsPointsAdjustmentsDetailsloading &&
      !rewardsPercentageDateLoading &&
      !rewardsTransactionLoding &&
      !loading
    ) {
      setUiReady(true);
    }
  }, [
    rewardsPointsAdjustmentsDetailsloading,
    rewardsPercentageDateLoading,
    rewardsTransactionLoding,
    loading,
  ]);
  useEffect(() => {
    // if (
    //   contactDetails?.availableCards &&
    //   contactDetails?.availableCards.length
    // ) {
    //   const formattedCards = contactDetails.availableCards.map((card: any) => ({
    //     ...card,
    //     cardNumber: card.cardNumber.substring(card.cardNumber.length - 4),
    //   }));

    //   const primaryCard = formattedCards.find(
    //     (card: any) => card.customerType.toLocaleLowerCase() === 'primary'
    //   );

    //   if (formattedCards.length === 1) {
    //     setSelectedCard(formattedCards[0]);
    //     setAvailableCards([formattedCards]);
    //   } else {
    //     if (primaryCard) {
    //       const allCardholdersOption = {
    //         ...primaryCard,
    //         nameOnCard: 'All cardholders',
    //       };
    //       setSelectedCard(allCardholdersOption);
    //       setAvailableCards([allCardholdersOption, ...formattedCards]);
    //     }
    //   }
    // }
    if (contactDetails) {
      if (
        contactDetails.availableCards &&
        contactDetails.availableCards.length > 0
      ) {
        setAvailableCards(contactDetails.availableCards);
        if (
          !selectedCard ||
          !contactDetails.availableCards.some(
            (card: any) => card?.customerId === selectedCard?.customerId
          )
        ) {
          setSelectedCard(contactDetails.availableCards[0]);
        }
      }
    }
  }, [contactDetails]);

  useEffect(() => {
    // Early return if essential Contentful data is not available
    if (
      !rb_earned_rewards_history?.rewardsHistory ||
      !rb_common?.commonString ||
      !accountDetails?.productName
    ) {
      console.log(
        'Contentful data not fully loaded yet, skipping percentage mapping'
      );
      return;
    }

    getRewardsHistoryEarnedCapBannerMessage(
      rb_earned_rewards_history,
      rb_common,
      rewardsPercentageDate,
      accountDetails,
      () => {},
      setCashBackPercent,
      setCashBackInfoTipDetails
    );
  }, [
    rewardsPercentageDate,
    rewardsPercentageDateFailure,
    lang,
    rb_earned_rewards_history,
    rb_common,
    accountDetails,
  ]);

  useEffect(() => {
    filterRewardsTransactionList();
  }, [rewardsTransactionList, rewardsPointsAdjustmentsDetails]);

  // Initialize cashBackPercent with default message when Contentful data becomes available
  useEffect(() => {
    if (
      rb_earned_rewards_history?.rewardsHistory?.earnedRewardsMessageTitle &&
      rb_earned_rewards_history?.rewardsHistory
        ?.earnedRewardsMessageWithNoRogers &&
      !cashBackPercent
    ) {
      setCashBackPercent(
        `${rb_earned_rewards_history.rewardsHistory.earnedRewardsMessageTitle}  ${rb_earned_rewards_history.rewardsHistory.earnedRewardsMessageWithNoRogers}`
      );
    }
  }, [rb_earned_rewards_history, cashBackPercent]);

  const tooltipTextMap = {
    month: rb_earned_rewards_history?.rewardsHistory?.thisMonthToolTip,
    bonus: rb_earned_rewards_history?.rewardsHistory?.bonusToolTip,
    earnedReward:
      rb_earned_rewards_history?.rewardsHistory?.earnedRewardsToolTip,
    adjustment: rb_earned_rewards_history?.rewardsHistory?.adjustmentsToolTips,
  };

  // Helper function to check if a promotion is a welcome bonus
  const getWelcomeBonusInfo = (promotion: any) => {
    if (!promotion?.promoEligible || !promotion?.id) {
      return null;
    }

    const welcomeBonuses =
      rb_earned_rewards_history?.rewardsHistory?.welcomeBonus;
    if (!welcomeBonuses || !Array.isArray(welcomeBonuses)) {
      return null;
    }

    const matchingBonus = welcomeBonuses.find(
      (bonus: any) => bonus.promotionId === promotion.id
    );

    return matchingBonus ? matchingBonus.promoLabel : null;
  };

  const formatSignedCurrency = (value: number, lang: string) => {
    if (!value || isNaN(value)) return '--';
    const absFormatted =
      webFormatCurrency(Math.abs(Number(value)), lang) || '--';
    return Number(value) < 0 ? `-${absFormatted}` : absFormatted;
  };

  const toRewardAmount = (value: number) =>
    Number((Number(value || 0) / 100).toFixed(2));

  const filterRewardsTransactionList = () => {
    let newEarnedRewards = 0;
    let newBonus = 0;
    let totalAdjustment = 0;

    if (rewardsTransactionList?.transactionsSearchDetails?.transactions) {
      const transactionList: RewardsTransaction[] =
        rewardsTransactionList.transactionsSearchDetails.transactions;

      if (transactionList && Array.isArray(transactionList)) {
        transactionList.forEach((item) => {
          if (
            accountDetails?.productName ===
              rb_common?.commonString?.fidoProductName ||
            accountDetails?.productName ===
              rb_common?.commonString?.platinumProductName
          ) {
            if (item?.promotions && Array.isArray(item.promotions)) {
              item.promotions.forEach((promotionItem) => {
                const roundedAmount = toRewardAmount(
                  promotionItem.pointsEarned
                );
                if (promotionItem.pointType === 'Base') {
                  newEarnedRewards += roundedAmount;
                } else if (promotionItem.pointType === 'Bonus') {
                  newBonus += roundedAmount;
                }
              });
            } else if (item?.basePointsEarned) {
              newEarnedRewards += toRewardAmount(item.basePointsEarned);
            }
          } else {
            if (item?.promotions && Array.isArray(item.promotions)) {
              item.promotions.forEach((promotionItem) => {
                const roundedAmount = toRewardAmount(
                  promotionItem.pointsEarned
                );
                if (promotionItem.pointType === 'Base') {
                  newEarnedRewards += roundedAmount;
                } else if (promotionItem.pointType === 'Bonus') {
                  newBonus += roundedAmount;
                }
              });
            }
          }
        });

        setIsEarnedRewards(newEarnedRewards + newBonus);
        setIsBonus(newBonus);
        setRewardsTransactionList(transactionList);
      } else {
        setIsEarnedRewards(0);
        setIsBonus(0);
        setIsThisMonth(0);
      }
    }

    if (
      rewardsPointsAdjustmentsDetails?.pointsAdjustmentsDetails
        ?.adjustmentDetails
    ) {
      const rewardsPointsAdjustment =
        rewardsPointsAdjustmentsDetails.pointsAdjustmentsDetails
          .adjustmentDetails;

      if (rewardsPointsAdjustment && Array.isArray(rewardsPointsAdjustment)) {
        const filterAdjustmentData = rewardsPointsAdjustment
          .map((item) => item?.adjustments)
          .filter((item) => item !== undefined);

        const validData =
          filterAdjustmentData.length > 0 ? filterAdjustmentData[0] : [];
        setIsAdjustmentDataList(validData || []);

        if (validData?.length > 0) {
          validData.forEach((item: any) => {
            totalAdjustment += toRewardAmount(item.adjustmentValue);
          });
          setIsAdjustments(totalAdjustment);
        } else {
          setIsAdjustments(0);
        }
      }
    }

    setIsThisMonth(newEarnedRewards + newBonus + totalAdjustment);
  };

  const updateStateAfterSelectionDropDown = () => {
    if (!selectedCard || !selectedPeriod || !accountDetails?.accountId) return;

    const yearToDateValue =
      rb_earned_rewards_history?.rewardsHistory?.yearToDate || '';

    const { firstDate, lastDate } = getFirstAndLastDate(
      selectedPeriod === yearToDateValue ? months[0] || '' : selectedPeriod,
      locale
    );

    const rewardsAdjestment = {
      accountId: accountDetails.accountId.replace(/^0+/, ''),
      cardLastFourDigits: selectedCard.cardNumber.slice(-4),
      userIdType: 'RANAC',
      fromDate: firstDate,
      toDate: lastDate,
      excludeOtherAccount: true,
      isYtd: selectedPeriod === yearToDateValue ? true : false,
    };
    dispatch(redeemRewardsPointsAdjustmentsDetailsRequest(rewardsAdjestment));

    const payloadData = {
      cardLastFourDigits: selectedCard.cardNumber.slice(-4),
      offset: offset,
      limit: limit,
      userId: accountDetails.accountId.replace(/^0+/, ''),
      userIdType: 'RANAC',
      fromDate: firstDate,
      toDate: lastDate,
      isYtd: selectedPeriod === yearToDateValue ? true : false,
    };
    dispatch(fetchRewardsTransactionRequest(payloadData));
  };

  const groupTransactionsByDate = (transactions: RewardsTransaction[]) => {
    if (!transactions || !Array.isArray(transactions)) return {};

    return transactions.reduce(
      (
        groups: { [postingDate: string]: RewardsTransaction[] },
        transaction
      ) => {
        const { postingDate } = transaction;
        if (!groups[postingDate]) {
          groups[postingDate] = [];
        }
        groups[postingDate].push(transaction);
        return groups;
      },
      {}
    );
  };

  const groupAdjustmentByDate = (transactions: AdjustmentsType[]) => {
    if (!transactions || !Array.isArray(transactions)) return {};

    return transactions.reduce(
      (
        groups: { [adjustmentDate: string]: AdjustmentsType[] },
        transaction
      ) => {
        const adjustmentDate = transaction?.adjustmentDate?.split('T')[0] || '';
        if (!groups[adjustmentDate]) {
          groups[adjustmentDate] = [];
        }
        groups[adjustmentDate].push(transaction);
        return groups;
      },
      {}
    );
  };

  const AdjustmentItem = ({ item }: { item: AdjustmentsType }) => (
    <div className="p-4 bg-white border-b border-[#E9E9E9]">
      <div className="flex justify-between">
        {/* <img
          src={assets?.rb_adjustment_icon}
          alt="Adjustment"
          className="w-6 h-6"
        /> */}
        <div className="flex-2">
          <p
            aria-label={item.adjustmentReason}
            className="font-semibold text-base"
          >
            {item.adjustmentReason}
          </p>
        </div>
        <div className="text-right">
          <p className="font-medium text-base">
            {Number(item.adjustmentValue) < 0
              ? `-${webFormatCurrency(Math.abs(Number(item.adjustmentValue / 100)), lang)}`
              : webFormatCurrency(Number(item.adjustmentValue / 100), lang) ||
                '--'}
          </p>
          <span className={'text-[#1E8240] text-xs'}>
            {`${
              Number(item.adjustmentValue) < 0
                ? `-${webFormatCurrency(Math.abs(Number(item.adjustmentValue / 100)), lang)}`
                : webFormatCurrency(Number(item.adjustmentValue / 100), lang) ||
                  '--'
            } ${rb_earned_rewards_history?.rewardsHistory?.cashBackAdded || ''}`}
          </span>
        </div>
      </div>
    </div>
  );

  const TransactionItem = ({
    item,
    promotions,
  }: {
    item: RewardsTransaction;
    promotions: any[];
  }) => {
    const isFidoOrPlatinum =
      accountDetails?.productName ===
        rb_common?.commonString?.fidoProductName ||
      accountDetails?.productName ===
        rb_common?.commonString?.platinumProductName;
    const renderPromotionLines = () => {
      if (isFidoOrPlatinum) {
        if (promotions && Array.isArray(promotions) && promotions.length > 0) {
          return promotions
            .filter(
              (promo) =>
                (promo?.pointsEarned || 0) + (item?.basePointsEarned || 0) !== 0
            )
            .map((promo, index) => {
              const welcomeBonusLabel = getWelcomeBonusInfo(promo);
              const checkRevertedValue = Number(promo?.pointsEarned) < 0;
              const amountLabel = formatSignedCurrency(
                toRewardAmount(promo?.pointsEarned),
                lang
              );
              const label =
                welcomeBonusLabel ||
                (promo.pointType ===
                rb_earned_rewards_history?.rewardsHistory?.base
                  ? checkRevertedValue
                    ? rb_earned_rewards_history?.rewardsHistory?.rewardDeducted
                    : rb_earned_rewards_history?.rewardsHistory?.rewardsEarned
                  : checkRevertedValue
                    ? rb_earned_rewards_history?.rewardsHistory?.rewardDeducted
                    : rb_earned_rewards_history?.rewardsHistory
                        ?.bonusRewardsEarned) ||
                '';
              return (
                <span
                  key={index}
                  className={`block ${index > 0 ? 'mt-1' : ''} text-[#1E8240] text-xs`}
                >
                  {`${amountLabel} ${label}`}
                </span>
              );
            });
        }
        if (!item?.basePointsEarned || item.basePointsEarned === 0) {
          return null;
        }
        const isReverted = Number(item.basePointsEarned) < 0;
        const amountLabel = formatSignedCurrency(
          toRewardAmount(item.basePointsEarned),
          lang
        );
        const label = isReverted
          ? rb_earned_rewards_history?.rewardsHistory?.rewardDeducted
          : rb_earned_rewards_history?.rewardsHistory?.rewardsEarned || '';
        return (
          <span className="block text-[#1E8240] text-xs">
            {`${amountLabel} ${label}`}
          </span>
        );
      }
      if (!(promotions && Array.isArray(promotions) && promotions.length > 0)) {
        return null;
      }
      return promotions
        .filter((promo) => promo.pointsEarned !== 0)
        .map((promo, index) => {
          const welcomeBonusLabel = getWelcomeBonusInfo(promo);
          const checkRevertedValue = Number(promo.pointsEarned) < 0;
          const amountLabel = formatSignedCurrency(
            toRewardAmount(promo.pointsEarned),
            lang
          );
          const label =
            welcomeBonusLabel ||
            (promo.pointType === rb_earned_rewards_history?.rewardsHistory?.base
              ? checkRevertedValue
                ? rb_earned_rewards_history?.rewardsHistory?.rewardDeducted
                : rb_earned_rewards_history?.rewardsHistory?.rewardsEarned
              : checkRevertedValue
                ? rb_earned_rewards_history?.rewardsHistory?.rewardDeducted
                : rb_earned_rewards_history?.rewardsHistory
                    ?.bonusRewardsEarned) ||
            '';
          return (
            <span
              key={index}
              className={`block ${index > 0 ? 'mt-1' : ''} text-[#1E8240] text-xs`}
            >
              {`${amountLabel} ${label}`}
            </span>
          );
        });
    };
    return (
      <div className="p-4 bg-white border-b border-[#E9E9E9]">
        <div className="flex justify-between">
          <div className="flex-2">
            <p
              aria-label={item.description}
              className="font-semibold text-base"
            >
              {item.description}
            </p>
            {item.productCode && (
              <span
                aria-label={item.productCode}
                className="text-gray-600 text-sm"
              >
                {item.productCode}
              </span>
            )}
          </div>
          <div className="text-right">
            <p className="font-semibold text-lg">
              {formatSignedCurrency(Number(item.billingAmount), lang)}
            </p>
            {renderPromotionLines()}
          </div>
        </div>
      </div>
    );
  };

  const isFidoOrPlatinum =
    accountDetails?.productName === rb_common?.commonString?.fidoProductName ||
    accountDetails?.productName ===
      rb_common?.commonString?.platinumProductName;

  const breadcrumbs = [
    { name: entries?.rb_navigation?.rewardsLogoTitle, href: '/rewards' },
    {
      name: entries?.rb_earned_rewards_history?.rewardsHistory
        ?.earnedRewardsHistoryTitle,
      href: '/view-rewards',
    },
  ];

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      <Breadcrumbs items={breadcrumbs} />
      <div className="w-full m-[22px] mx-auto rounded-[15px] bg-white min-h-[70vh] my-0">
        <div className="bg-white md:p-6 p-4 rounded-lg">
          {rewardsTransactionLoding ||
          rewardsPercentageDateLoading ||
          rewardsPointsAdjustmentsDetailsloading ||
          loading ||
          !UiReady ? (
            <div className="flex justify-center items-center h-[80vh]">
              <Spinner />
            </div>
          ) : (
            <>
              <h2
                aria-label={
                  entries?.rb_earned_rewards_history?.rewardsHistory
                    ?.earnedRewardsHistoryTitle
                }
                className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] md:pb-7 pb-1"
              >
                {
                  entries?.rb_earned_rewards_history?.rewardsHistory
                    ?.earnedRewardsHistoryTitle
                }
              </h2>

              <div className="bg-white md:bg-[#f5f5f5] rounded-lg shadow lg:flex lg:items-center">
                {availableCards?.length > 1 && (
                  <div className="w-full lg:w-1/3 md:w-2/3 px-4 pt-6 md:px-6 border-b md:border-b-0 relative">
                    <div className="text-[#1F1F1F] text-[15px] font-normal leading-[24px] mb-2">
                      {rb_transaction_history?.showingTitle}
                    </div>
                    <CardDropdown
                      className="w-full lg:mb-4"
                      availableCards={availableCards}
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                      // showAllCardholdersOption={true}
                    />
                  </div>
                )}
                <div className="w-full lg:w-1/3 md:w-2/3 px-4 py-3 md:px-6 lg:py-5 border-b md:border-b-0 md:pr-5 relative">
                  <p
                    className="text-[#1f1f1f] text-sm"
                    aria-label={
                      rb_earned_rewards_history?.rewardsHistory?.period
                    }
                  >
                    {rb_earned_rewards_history?.rewardsHistory?.period || ''}
                  </p>
                  <button
                    className="w-full py-3 border-b border-black text-left"
                    onClick={handleDropdownToggle}
                    ref={buttonRef}
                  >
                    <div className="flex justify-between items-center">
                      <p
                        className="text-[#1f1f1f] text-base"
                        aria-label={selectedPeriod}
                      >
                        {selectedPeriod}
                      </p>
                      <img
                        src={assets?.down_arrow}
                        alt="downArrow"
                        className="w-6 h-6"
                      />
                    </div>
                  </button>

                  {isMonthsDropdownOpen && (
                    <div
                      ref={dropdownRef}
                      className="absolute mt-2 w-4/5 max-h-40 overflow-y-auto bg-white shadow-lg border rounded-md z-10"
                    >
                      <ul>
                        {months.map((month: any, index: any) => (
                          <li
                            key={index}
                            onClick={() => handleMonthSelect(month)}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                          >
                            {month}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {(rewardsPercentageDate?.statusCode === 200 ||
                  rewardsPercentageDate?.statusCode === 422) && 
                  rb_feature_flagging?.featureFlagging?.rewards?.earn_cap_banner_web && (
                  <div className="w-full lg:w-2/3 bg-gray-100 px-4 py-4 md:px-6 lg:pl-6 lg:pt-5">
                    <div className="flex items-start gap-0">
                      <p className="text-base text-[#1f1f1f] flex-1">
                        {cashBackPercent}
                      </p>

                      <CommanToolTip
                        label=""
                        tooltipText={cashBackInfoTipDetails}
                        isVisible={false}
                        fill="#DA291C"
                        toolTipStyle={{ maxWidth: '250px', left: '-220px' }}
                        display="flex"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-4 lg:flex lg:flex-row w-full mt-4">
                <FlippedToolTip
                  tileId="month"
                  title={rb_earned_rewards_history?.rewardsHistory?.thisMonth}
                  value={
                    Number(isThisMonth) < 0
                      ? `-${webFormatCurrency(Math.abs(Number(isThisMonth)), lang)}`
                      : webFormatCurrency(Number(isThisMonth), lang) || '--'
                  }
                  icon={assets?.rb_month_icon}
                  tooltipTextMap={tooltipTextMap}
                  lang={lang}
                  activeTile={activeTile}
                  setActiveTile={setActiveTile}
                  showTooltipMobile={showTooltipMobile}
                  setShowTooltipMobile={setShowTooltipMobile}
                />
                {/* <FlippedToolTip
                  tileId="bonus"
                  title={rb_earned_rewards_history?.rewardsHistory?.bonus}
                  value={
                    Number(isBonus) < 0
                      ? `-${webFormatCurrency(Math.abs(Number(isBonus)), lang)}`
                      : webFormatCurrency(Number(isBonus), lang) || '--'
                  }
                  icon={assets?.rb_bonus_icon}
                  tooltipTextMap={tooltipTextMap}
                  lang={lang}
                  activeTile={activeTile}
                  setActiveTile={setActiveTile}
                  showTooltipMobile={showTooltipMobile}
                  setShowTooltipMobile={setShowTooltipMobile}
                /> */}
                <FlippedToolTip
                  tileId="earnedReward"
                  title={
                    rb_earned_rewards_history?.rewardsHistory?.earnedRewards
                  }
                  value={
                    Number(isEarnedRewards) < 0
                      ? `-${webFormatCurrency(Math.abs(Number(isEarnedRewards)), lang)}`
                      : webFormatCurrency(Number(isEarnedRewards), lang) || '--'
                  }
                  icon={assets?.selfServeBenefits}
                  tooltipTextMap={tooltipTextMap}
                  lang={lang}
                  activeTile={activeTile}
                  setActiveTile={setActiveTile}
                  showTooltipMobile={showTooltipMobile}
                  setShowTooltipMobile={setShowTooltipMobile}
                />
                <FlippedToolTip
                  tileId="adjustment"
                  title={rb_earned_rewards_history?.rewardsHistory?.adjustments}
                  value={
                    Number(isAdjustments) < 0
                      ? `-${webFormatCurrency(Math.abs(Number(isAdjustments)), lang)}`
                      : webFormatCurrency(Number(isAdjustments), lang) || '--'
                  }
                  icon={assets?.rb_adjustment_icon}
                  tooltipTextMap={tooltipTextMap}
                  lang={lang}
                  activeTile={activeTile}
                  setActiveTile={setActiveTile}
                  showTooltipMobile={showTooltipMobile}
                  setShowTooltipMobile={setShowTooltipMobile}
                />
              </div>

              <div className="md:hidden block">
                {isRewardsTransactionList.length > 0 &&
                  Object.keys(
                    groupTransactionsByDate(isRewardsTransactionList)
                  ).map((date) => (
                    <div key={date}>
                      <div className="flex flex-row px-4 py-2 items-center text-left bg-gray-200 min-h-[32px]">
                        <span className="text-gray-800 text-left text-sm">
                          {formatDate(date, 'dddd, MMMM D, YYYY')}
                        </span>
                      </div>

                      {groupTransactionsByDate(isRewardsTransactionList)[
                        date
                      ].map((transaction) => (
                        <TransactionItem
                          key={
                            transaction.sequenceNumber ||
                            Math.random().toString()
                          }
                          item={transaction}
                          promotions={transaction.promotions || []}
                        />
                      ))}
                    </div>
                  ))}

                {isAdjustmentDataList.length > 0 &&
                  Object.keys(groupAdjustmentByDate(isAdjustmentDataList)).map(
                    (adjustmentDate) => (
                      <div key={adjustmentDate}>
                        <div className="flex flex-row px-4 py-2 items-center text-left bg-gray-200 min-h-[32px]">
                          <span
                            className="text-gray-800 text-left text-sm"
                            aria-label={adjustmentDate}
                          >
                            {formatDate(adjustmentDate, 'dddd, MMMM D, YYYY')}
                          </span>
                        </div>

                        {groupAdjustmentByDate(isAdjustmentDataList)[
                          adjustmentDate
                        ].map((transaction: any, index: any) => (
                          <AdjustmentItem
                            key={
                              transaction.adjustmentValue
                                ? transaction.adjustmentValue.toString() + index
                                : index.toString()
                            }
                            item={transaction}
                          />
                        ))}
                      </div>
                    )
                  )}
              </div>

              {(isAdjustmentDataList?.length > 0 ||
                isRewardsTransactionList?.length > 0) && (
                <div className="hidden md:block mt-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead className="">
                        <tr>
                          <th
                            aria-label={
                              rb_earned_rewards_history?.rewardsHistory?.date
                            }
                            className="p-4 bg-white border-b text-left text-base font-medium text-[#666666]"
                          >
                            {rb_earned_rewards_history?.rewardsHistory?.date}
                          </th>
                          <th
                            aria-label={
                              rb_earned_rewards_history?.rewardsHistory
                                ?.transactionDescription
                            }
                            className="p-4 bg-white border-b text-left text-base font-medium text-[#666666]"
                          >
                            {
                              rb_earned_rewards_history?.rewardsHistory
                                ?.transactionDescription
                            }
                          </th>
                          {/* {selectedCard.nameOnCard === 'All cardholders' && (
                            <th className="p-4">
                              {
                                rb_earned_rewards_history?.rewardsHistory
                                  ?.cardHolder
                              }
                            </th>
                          )} */}
                          <th
                            aria-label={
                              rb_earned_rewards_history?.rewardsHistory?.amount
                            }
                            className="p-4 bg-white border-b text-left text-base font-medium text-[#666666]"
                          >
                            {rb_earned_rewards_history?.rewardsHistory?.amount}
                          </th>
                          <th
                            aria-label={
                              rb_earned_rewards_history?.rewardsHistory
                                ?.cashBack
                            }
                            className="p-4 bg-white border-b text-left text-base font-medium text-[#666666]"
                          >
                            {
                              rb_earned_rewards_history?.rewardsHistory
                                ?.cashBack
                            }
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-base text-gray-800">
                        {isRewardsTransactionList.map((item) => (
                          <tr
                            key={
                              item.sequenceNumber || Math.random().toString()
                            }
                            className="border-b"
                          >
                            <td className="p-4" aria-label={item.postingDate}>
                              {formatDate(item.postingDate, 'MMM D, YYYY')}
                            </td>
                            <td aria-label={item.description} className="p-4">
                              {item.description}
                            </td>
                            {/* {selectedCard.nameOnCard === 'All cardholders' && (
                              <td className="p-4">
                                {currentUser.toLowerCase() ==
                                item?.merchant?.shortName?.toLowerCase()
                                  ? ''
                                  : item?.merchant?.shortName?.toLowerCase()}
                              </td>
                            )} */}
                            <td className="p-4">
                              {formatSignedCurrency(
                                Number(item.billingAmount),
                                lang
                              )}
                            </td>
                            <td className="p-4 flex flex-col">
                              {isFidoOrPlatinum ? (
                                item.promotions &&
                                Array.isArray(item.promotions) &&
                                item.promotions.length > 0 ? (
                                  <span className="block">
                                    {item.promotions
                                      .filter(
                                        (promo) =>
                                          (promo.pointsEarned || 0) +
                                            (item.basePointsEarned || 0) !==
                                          0
                                      )
                                      .map((promotion, idx) => {
                                        const welcomeBonusLabel =
                                          getWelcomeBonusInfo(promotion);
                                        const checkRevertedValue =
                                          Number(promotion.pointsEarned) < 0;
                                        const totalPoints =
                                          (promotion.pointsEarned || 0) +
                                          (item.basePointsEarned || 0);
                                        const amountLabel =
                                          formatSignedCurrency(
                                            Number(totalPoints) / 100,
                                            lang
                                          );
                                        const isBaseType =
                                          promotion.pointType ===
                                            rb_earned_rewards_history
                                              ?.rewardsHistory?.base ||
                                          promotion.pointType === 'Base';
                                        const label =
                                          welcomeBonusLabel ||
                                          (isBaseType
                                            ? checkRevertedValue
                                              ? rb_earned_rewards_history
                                                  ?.rewardsHistory
                                                  ?.rewardDeducted
                                              : rb_earned_rewards_history
                                                  ?.rewardsHistory
                                                  ?.rewardsEarned
                                            : checkRevertedValue
                                              ? rb_earned_rewards_history
                                                  ?.rewardsHistory
                                                  ?.rewardDeducted
                                              : rb_earned_rewards_history
                                                  ?.rewardsHistory
                                                  ?.bonusRewardsEarned);
                                        return (
                                          <div
                                            key={idx}
                                            className={`${idx > 0 ? 'mt-2' : ''} text-[#1E8240]`}
                                          >
                                            <div className="text-lg">
                                              {amountLabel}
                                            </div>
                                            <div className="text-sm">
                                              {label}
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </span>
                                ) : item.basePointsEarned &&
                                  item.basePointsEarned !== 0 ? (
                                  <div className="text-[#1E8240]">
                                    <div className="text-lg">
                                      {webFormatCurrency(
                                        Number(item.basePointsEarned) / 100,
                                        lang
                                      ) || '--'}
                                    </div>
                                    <div className="text-sm">
                                      {Number(item.basePointsEarned) < 0
                                        ? rb_earned_rewards_history
                                            ?.rewardsHistory?.rewardDeducted
                                        : rb_earned_rewards_history
                                            ?.rewardsHistory?.rewardsEarned}
                                    </div>
                                  </div>
                                ) : (
                                  '-'
                                )
                              ) : item.promotions &&
                                Array.isArray(item.promotions) &&
                                item.promotions.length > 0 ? (
                                <span className="block">
                                  {item.promotions
                                    .filter((promo) => promo.pointsEarned !== 0)
                                    .map((promotion, idx) => {
                                      const welcomeBonusLabel =
                                        getWelcomeBonusInfo(promotion);
                                      const checkRevertedValue =
                                        Number(promotion.pointsEarned) < 0;
                                      const amountLabel = formatSignedCurrency(
                                        Number(promotion?.pointsEarned) / 100,
                                        lang
                                      );
                                      const isBaseType =
                                        promotion.pointType ===
                                          rb_earned_rewards_history
                                            ?.rewardsHistory?.base ||
                                        promotion.pointType === 'Base';
                                      const label =
                                        welcomeBonusLabel ||
                                        (isBaseType
                                          ? checkRevertedValue
                                            ? rb_earned_rewards_history
                                                ?.rewardsHistory?.rewardDeducted
                                            : rb_earned_rewards_history
                                                ?.rewardsHistory?.rewardsEarned
                                          : checkRevertedValue
                                            ? rb_earned_rewards_history
                                                ?.rewardsHistory?.rewardDeducted
                                            : rb_earned_rewards_history
                                                ?.rewardsHistory
                                                ?.bonusRewardsEarned);
                                      return (
                                        <div
                                          key={idx}
                                          className={`${idx > 0 ? 'mt-2' : ''} text-[#1E8240]`}
                                        >
                                          <div className="text-lg">
                                            {amountLabel}
                                          </div>
                                          <div className="text-sm">{label}</div>
                                        </div>
                                      );
                                    })}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        ))}

                        {isAdjustmentDataList.length > 0 &&
                          isAdjustmentDataList.map((adjustment, idx) => (
                            <tr key={`adjustment-${idx}`} className="border-b">
                              <td
                                className="p-4"
                                aria-label={adjustment.adjustmentDate}
                              >
                                {formatDate(
                                  adjustment.adjustmentDate,
                                  'MMM D, YYYY'
                                )}
                              </td>
                              <td
                                className="p-4"
                                aria-label={adjustment.adjustmentReason}
                              >
                                {adjustment.adjustmentReason}
                              </td>
                              {/* <td className="p-4">-</td> */}
                              <td className="p-4">
                                {Number(adjustment.adjustmentValue) < 0
                                  ? `-${webFormatCurrency(Math.abs(Number(adjustment.adjustmentValue / 100)), lang)}`
                                  : webFormatCurrency(
                                      Number(adjustment.adjustmentValue / 100),
                                      lang
                                    ) || '--'}
                              </td>
                              <td
                                className={`p-4 text-[#1E8240] flex-col flex`}
                              >
                                <div className="text-base font-medium">
                                  {Number(adjustment.adjustmentValue) < 0
                                    ? `-${webFormatCurrency(Math.abs(Number(adjustment.adjustmentValue / 100)), lang)}`
                                    : webFormatCurrency(
                                        Number(
                                          adjustment.adjustmentValue / 100
                                        ),
                                        lang
                                      ) || '--'}
                                </div>
                                <div className="text-sm">
                                  {rb_earned_rewards_history?.rewardsHistory
                                    ?.cashBackAdded || ''}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {rewardsTransactionList?.transactionsSearchDetails?.total > 0 && (
                <div className="flex flex-col justify-center items-center gap-4 mt-6 mb-6">
                  {/* {limit <
                    rewardsTransactionList.transactionsSearchDetails.total && (
                    <button
                      className="bg-white text-base font-medium text-red-600 border border-red-600 px-4 py-2 hover:bg-red-50"
                      onClick={() => {
                        setOffset((offset: number) => offset + 1);
                        setLimit((limit: number) => limit + 25);
                        setTimeout(() => {
                          window.scrollTo({
                            top: document.documentElement.scrollHeight,
                            behavior: 'smooth',
                          });
                        }, 300);
                      }}
                      aria-label={
                        rb_earned_rewards_history?.rewardsHistory?.showMore
                      }
                    >
                      {rb_earned_rewards_history?.rewardsHistory?.showMore}
                    </button>
                  )} */}

                  <button
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-red-500 text-base font-normal hover:underline"
                    aria-label={
                      rb_earned_rewards_history?.rewardsHistory?.goTop
                    }
                  >
                    {rb_earned_rewards_history?.rewardsHistory?.goTop}
                  </button>
                </div>
              )}

              <div className="text-center">
                {!(
                  isAdjustmentDataList?.length > 0 ||
                  isRewardsTransactionList?.length > 0
                ) && (
                  <>
                    <p
                      className="text-[15px] text-[#1F1F1F] mt-4 font-medium"
                      aria-label={
                        rb_earned_rewards_history?.rewardsHistory
                          ?.noRewardsFoundTitle
                      }
                    >
                      {rb_earned_rewards_history?.rewardsHistory
                        ?.noRewardsFoundTitle || ''}
                    </p>
                    <p
                      className="text-[12px] text-[#666] pb-4"
                      aria-label={
                        rb_earned_rewards_history?.rewardsHistory
                          ?.noRewardsFoundMsg
                      }
                    >
                      {rb_earned_rewards_history?.rewardsHistory
                        ?.noRewardsFoundMsg || ''}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRewards;
